import React, { useCallback } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field as FormField } from 'react-final-form';
import { useTokenizedApi } from '@tokenized/sdk-react-private';
import Modal from '#components/Layouts/Modal/Modal/Modal';
import Box from '#components/Elements/Box/Box';
import Spinner from '#components/Elements/Spinner/Spinner';
import ModalIcon from '#components/Elements/SVGIcon/ModalIcon';
import Tooltip from '#components/Elements/Tooltip/Tooltip';
import Label from '#components/Elements/Form/Field/Label';
import Input from '#components/Elements/Form/Controls/Input';
import Button from '#components/Elements/Button/Button';
import TextLink from '#components/Elements/TextLink/TextLink';
import {
  useValidators,
  fieldIsRequired,
  QUIET_VALIDATION_FAILURE,
} from '#modules/utils/validatorsLocalized';
import modalLayoutStyles from '#components/Layouts/Modal/ModalLayout/ModalLayout.scss';
import modalFooterStyles from '#components/Layouts/Modal/ModalForm/ModalForm.scss';
import dialogStyles from './Dialog.scss';
import { MFA_TYPE_EMAIL } from '#modules/api/User';

export function DialogHeader({
  className,
  children,
  onCloseModal,
  fullscreen,
}) {
  return (
    <Modal.Card.Head className={className} showClose={false}>
      <div className={modalLayoutStyles.modalHeader} data-test="modal-title">
        {children}
      </div>
      <div className={modalLayoutStyles.headerButtons}>
        {!!onCloseModal && (
          <Button
            icon
            className={classNames(
              modalLayoutStyles.modalCloseButton,
              fullscreen && 'no-drag',
            )}
            onClick={onCloseModal}
            data-test="closeModal"
          >
            <ModalIcon type="x" />
          </Button>
        )}
      </div>
    </Modal.Card.Head>
  );
}

export function DialogBody({
  className,
  noMargins,
  noFooter,
  children,
  onCloseModal,
  ...props
}) {
  return (
    <Modal.Card.Body
      className={classNames(
        className,
        modalLayoutStyles.modalCardBody,
        noFooter && modalLayoutStyles.noFooter,
      )}
    >
      <div
        className={classNames(
          modalLayoutStyles.simpleDialogBodyContent,
          noMargins && modalLayoutStyles.noMargins,
          noFooter && modalLayoutStyles.noFooter,
        )}
        {...props}
      >
        {children}
      </div>
    </Modal.Card.Body>
  );
}

export function DialogFooter({ className, children, test, testDialogChoices }) {
  return (
    <Modal.Card.Foot className={className}>
      <div
        className={modalFooterStyles.fullwidth}
        data-test={test}
        data-dialog-choices={testDialogChoices}
      >
        <Box flush={['top']} className={modalFooterStyles.reset}>
          <div className={modalFooterStyles.verticalContent}>
            <div className={modalFooterStyles.content}>{children}</div>
          </div>
        </Box>
      </div>
    </Modal.Card.Foot>
  );
}

export function DialogFooterMfa({
  className,
  isDanger,
  mfaRequest,
  onCancel,
  loggingIn,
}) {
  const cancelAction = () => {
    onCancel?.();
    mfaRequest?.cancel?.();
  };
  const tokenizedApi = useTokenizedApi();
  const mobileAppRecovery = useCallback(
    (event) => {
      event.preventDefault();
      tokenizedApi.account.initiateDevicePairing();
    },
    [tokenizedApi],
  );
  return (
    <Modal.Card.Foot className={className}>
      <div
        className={classNames(
          modalFooterStyles.ModalFooterPending,
          isDanger && modalFooterStyles.isDanger,
        )}
        data-test="confirming"
        data-dialog-choices="confirm-mobile"
      >
        <Box flush={['top']} className={modalFooterStyles.actionContainer}>
          <Button
            className={modalFooterStyles.leftGroup}
            inverted
            transparent
            color="subdued"
            onClick={cancelAction}
            data-action="cancel-confirmation"
          >
            <FormattedMessage
              defaultMessage="Cancel"
              description="MFA footer cancel button"
            />
          </Button>
          <div className={modalFooterStyles.rightGroup}>
            <div className={modalFooterStyles.description}>
              {loggingIn && (
                <TextLink
                  className={modalFooterStyles.mfaHelp}
                  onClick={mobileAppRecovery}
                  inverted
                  data-test="recovery"
                  data-action="re-pair-mobile"
                >
                  <FormattedMessage
                    defaultMessage="Trouble?"
                    description="Mobile authentication help tooltip"
                  />
                </TextLink>
              )}
              <div
                data-action="__text-content__"
                className={classNames(
                  'is-loading',
                  modalFooterStyles.confirmWithApp,
                )}
              >
                <FormattedMessage
                  defaultMessage="Confirm with the mobile app"
                  description="Dialog footer MFA prompt"
                />
              </div>
            </div>
            {mfaRequest && (
              <Tooltip
                wrapperClassName={modalFooterStyles.mfaPillWrapper}
                maxWidth={254}
                location="south"
                text={
                  <FormattedMessage
                    defaultMessage="Verify this code matches your mobile confirmation request"
                    description="Mobile confirmation code label"
                  />
                }
              >
                <div className={modalFooterStyles.mfaPill} data-test="mfa-code">
                  {mfaRequest.confirmationCode}
                </div>
              </Tooltip>
            )}
            <div className={modalFooterStyles.spinnerContainer}>
              <Spinner className={modalFooterStyles.actionSpinner} />
            </div>
          </div>
        </Box>
      </div>
    </Modal.Card.Foot>
  );
}

export function LiquidNobleDialogMfaFooter({
  className,
  onCancel,
  isDanger,
  mfaRequest,
  loading,
}) {
  const intl = useIntl();
  const validateRequired = useValidators(fieldIsRequired);
  const cancelAction = () => {
    onCancel?.();
  };
  return (
    <Modal.Card.Foot className={className}>
      <div
        className={classNames(
          modalFooterStyles.ModalFooterPending,
          isDanger && modalFooterStyles.isDanger,
        )}
        data-test="confirming"
        data-dialog-choices="confirm-mobile"
      >
        <Box flush={['top']} className={modalFooterStyles.actionContainer}>
          <div className={modalFooterStyles.leftGroup}>
            <Button
              className={dialogStyles.mobileHidden}
              inverted
              transparent
              color="subdued"
              onClick={cancelAction}
              data-action="cancel-confirmation"
            >
              <FormattedMessage
                defaultMessage="Cancel"
                description="MFA footer cancel button"
              />
            </Button>
          </div>
          <div className={modalFooterStyles.rightGroup}>
            <div
              className={classNames(
                modalFooterStyles.description,
                dialogStyles.mfaInputBlock,
              )}
            >
              <div>
                <FormField name="mfaCode" validate={validateRequired}>
                  {({
                    input,
                    meta: { touched, error, submitError, dirtySinceLastSubmit },
                  }) => (
                    <Label>
                      <Input
                        disableOnFocus
                        disableAutocomplete
                        type="text"
                        placeholder={
                          mfaRequest === MFA_TYPE_EMAIL
                            ? intl.formatMessage({
                                defaultMessage: 'Enter emailed MFA code',
                              })
                            : intl.formatMessage({
                                defaultMessage: 'Enter the MFA app code',
                              })
                        }
                        size="medium"
                        maxLength={16}
                        color={
                          (touched &&
                            error &&
                            error !== QUIET_VALIDATION_FAILURE) ||
                          (submitError && !dirtySinceLastSubmit)
                            ? 'danger'
                            : null
                        }
                        {...input}
                      />
                    </Label>
                  )}
                </FormField>
              </div>
            </div>
            <Button
              submit
              inverted
              transparent
              loading={loading}
              color="subdued"
              data-action="submit-confirmation"
              className={dialogStyles.confirmMfaButton}
            >
              <FormattedMessage
                defaultMessage="Confirm"
                description="MFA footer confirm button"
              />
            </Button>
          </div>
        </Box>
      </div>
    </Modal.Card.Foot>
  );
}

export function DialogFooterInProgress({
  className,
  isDanger,
  children,
  onCancel,
}) {
  const cancelAction = () => {
    onCancel?.();
  };
  return (
    <Modal.Card.Foot className={className}>
      <div
        className={classNames(
          modalFooterStyles.ModalFooterPending,
          isDanger && modalFooterStyles.isDanger,
        )}
        data-dialog-choices="logging-in"
      >
        <Box flush={['top']} className={modalFooterStyles.actionContainer}>
          <Button
            className={modalFooterStyles.leftGroup}
            inverted
            transparent
            color="subdued"
            onClick={cancelAction}
            data-action="cancel-log-in"
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <div className={modalFooterStyles.rightGroup}>
            <div
              className={classNames(
                'is-loading',
                modalFooterStyles.description,
              )}
              data-action="__text-content__"
            >
              {children}
            </div>
            <div className={modalFooterStyles.spinnerContainer}>
              <Spinner className={modalFooterStyles.actionSpinner} />
            </div>
          </div>
        </Box>
      </div>
    </Modal.Card.Foot>
  );
}
